import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import {
	getEntityById,
	getStatusLabelFromPipelineFilters,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { Button, Popconfirm } from 'antd';
import {
	cycleEventsRestCrudThunksForBuyer,
	cycleEventsRestCrudThunksForSupplier,
} from '../../thunks/cycle_events_thunks';
import GeneralStatusDisplay from '../AdminCycleEventsIndexPage/GeneralStatusDisplay';
import CycleEventPipeLineFilters from '../AdminCycleEventsIndexPage/CycleEventPipelineFilters';
import { ACC_RUN_STATUSES } from '../../utils/AssetCycleUtils';
import DateTimeHover from '../date_time_component/DateTime';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';

interface AssetCycleCountEventDetailPageProps {
	match: any;
	location: any;
	history: any;
	cycleEvent: any;
	cycleRunsFetching: boolean;
	companyConfig: any;
	getCycleEvent(id: number | string): Promise<any>;
	childRoutes: any[];
	deleteCycleEvent(cycleEvent: any): Promise<any>;
	userType: string;
}

const EDITABLE_STATUSES = ['notStarted', 'inProgress'];

const AssetCycleCountEventDetailPage: FC<AssetCycleCountEventDetailPageProps> = ({
	match,
	history,
	companyConfig,
	getCycleEvent,
	childRoutes,
	cycleEvent,
	deleteCycleEvent,
	userType,
}): React.ReactElement => {
	const cycleRunId = useMemo(() => match.params.id, [match.params.id]);

	const defaultPath = `/${userType}/workflows/cycleevents`;

	useEffect(() => {
		getCycleEvent(cycleRunId);
	}, [cycleRunId]);

	const runCounts = nullSafeGetOrElse('assetCycleCountRunCountsBasedOnStatus', cycleEvent, []);
	const completedEntry = runCounts.find(function (c) {
		return c.categoryName === ACC_RUN_STATUSES.CHANGES_APPLIED;
	});
	const completedCount = nullSafeGetOrElse('count', completedEntry, 0);

	const isEditEnabled = useMemo(
		() => EDITABLE_STATUSES.includes(nullSafeGetOrElse('status', cycleEvent, '')),
		[cycleEvent]
	);

	return (
		<div>
			<DetailPageHeader
				exactPath={`/${userType}/assetcycleevents/detail/:id`}
				redirectPath={`/${userType}/assetcycleevents/detail/${cycleRunId}/details`}
				backLinkText={'Back to Asset Cycle Runs'}
				backLinkTo={defaultPath}
				title={nullSafeGet('name', cycleEvent) || ''}
				subtitle={
					<div className="flex flex-row items-center gap-2">
						<GeneralStatusDisplay
							statusKey={nullSafeGet('status', cycleEvent)}
							statusLabel={getStatusLabelFromPipelineFilters(
								CycleEventPipeLineFilters,
								nullSafeGet('status', cycleEvent)
							)}
							className="footnoteDescription"
						/>
						<div className="footnoteregular text-white">
							{completedCount}/{nullSafeGetOrElse('locationCount', cycleEvent, 0)} Runs complete
						</div>
						<div className="footnoteregular text-white">
							Due:{' '}
							<DateTimeHover
								showDate
								timestamp={nullSafeGet('dueAt', cycleEvent)}
								showTime={false}
							/>
						</div>
					</div>
				}
				actions={[
					<Button
						key="edit"
						ghost
						disabled={!isEditEnabled}
						onClick={() => history.push(`/${userType}/assetcycleevents/edit/${cycleRunId}`)}
					>
						Edit
					</Button>,
					<Popconfirm
						title="Are you sure you want to delete this asset cycle event?"
						onConfirm={() => deleteCycleEvent(cycleEvent).then(() => history.push(defaultPath))}
					>
						<Button key="delete" ghost className="ml-4">
							Delete
						</Button>
					</Popconfirm>,
				]}
				tabs={[]}
				backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
				childRoutes={childRoutes}
			/>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	cycleEvent: getEntityById(ownProps.match.params.id, state.cycle_events),
	companyConfig: state.company_config,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getCycleEvent: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier.readOne(id)
				: cycleEventsRestCrudThunksForBuyer.readOne(id)
		),
	deleteCycleEvent: (cycleEvent) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier.delete(cycleEvent)
				: cycleEventsRestCrudThunksForBuyer.delete(cycleEvent)
		),
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(AssetCycleCountEventDetailPage as any))
);
